import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { FormProvider } from 'react-hook-form';
import {
  Button,
  FormActions,
  FormLayout,
  MoneyInput,
  FormRow as Row,
  FormSection as Section,
} from '~/components';
import { DatePicker, Select, TextArea, TextInput } from '../../../components';
import { useCustomForm } from '../../../hooks/useCustomForm';
import { useCMETrainingRef } from '../account/vaultSelectors';
import { CREDIT_TYPE_OPTIONS } from './constants';
import { CMETrainingFormValues, cmeTrainingSchema, CreateCMETrainingFormValues } from './types';

const TAGS = CT.cmeTraining.form;

interface FormProps {
  form: Partial<CMETrainingFormValues>;
  create: boolean;
}

export const CMETrainingForm = (props: FormProps) => {
  const { form, create } = props;
  const { cmeTrainingRef, addCME, editCME, closeModal } = useCMETrainingRef();

  const methods = useCustomForm<CMETrainingFormValues>({
    resolver: zodResolver(cmeTrainingSchema),
    defaultValues: form,
  });
  const { handleSubmit } = methods;

  if (!cmeTrainingRef) {
    return null;
  }

  const onSubmitSuccess = (values: CMETrainingFormValues | CreateCMETrainingFormValues) => {
    // NOTE: this is done to avoid issues with `creditsEarned` typing in the zod schema.
    // which would be a more appropriate place to handle this.
    const processedValues = {
      ...values,
      creditsEarned: Number(values.creditsEarned) || 0,
    };
    if (create) {
      addCME(processedValues as CreateCMETrainingFormValues);
    } else {
      editCME(processedValues as CMETrainingFormValues);
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('CME Training form Submit Error: ', err);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          data-cy={TAGS.form}
          onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
          id="training-form"
        >
          <FormLayout>
            <Section componentsPerLine={2}>
              <Row>
                <DatePicker
                  name={'activityDate'}
                  label={'Activity Date*'}
                  cyTag={TAGS.activityDatePicker}
                />
              </Row>
              <Row>
                <Select
                  name={'creditType'}
                  label={'Credit Type*'}
                  cyTag={TAGS.typeSelect}
                  options={CREDIT_TYPE_OPTIONS}
                />
              </Row>
              <Row>
                <TextInput name={'courseTitle'} label={'Course Title*'} cyTag={TAGS.titleInput} />
              </Row>
              <Row>
                <TextInput
                  name={'certReceived'}
                  label={'Certificate Received'}
                  cyTag={TAGS.certInput}
                />
              </Row>
              <Row>
                <TextInput
                  name={'eduProviderName'}
                  label={'Education Provider'}
                  cyTag={TAGS.providerInput}
                />
              </Row>
              <Row>
                <MoneyInput name={'cost'} label={'Cost'} cyTag={TAGS.costInput} />
              </Row>
              <Row>
                <TextInput
                  name={'creditsEarned'}
                  label={'Credits Earned'}
                  cyTag={TAGS.creditsInput}
                  type={'number'}
                  min={0}
                />
              </Row>
              <Row>
                <TextInput name={'location'} label={'Location'} cyTag={TAGS.location} />
              </Row>
            </Section>
            <Section componentsPerLine={1}>
              <TextArea name={'details'} label={'Details'} cyTag={TAGS.details} />
            </Section>
            <FormActions>
              <Button
                cyTag={TAGS.closeBtn}
                label="Close"
                type="button"
                onClick={closeModal}
                colorVariant="secondary"
              />
              <Button cyTag={TAGS.submitBtn} label="Save" type="submit" />
            </FormActions>
          </FormLayout>
        </form>
      </FormProvider>
    </>
  );
};
